import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import {
  CHART_GROUP_BY_OPTIONS,
  CHART_PERIOD_OPTIONS,
  CHART_TYPE_OPTIONS,
  type ChartableQuestion,
  type ChartType,
  type SurveyChartConfig,
  type SurveyChartConfigBody,
} from '../consts';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSave: (payload: { name: string; config: SurveyChartConfigBody }) => void;
  // Discrete-value questions - the only ones chartable outside RESPONSES.
  // Used to validate the Question picker (which always offers allQuestions)
  // against the selected chart type - see handleValidate.
  chartableQuestions: ChartableQuestion[];
  allQuestions: ChartableQuestion[];
  // Set to edit an existing chart; omit/null to create a new one.
  existing?: SurveyChartConfig | null;
}

// Trend group-bys (anything but "single") have no meaningful distribution
// shape to render as a doughnut/bar - only a line makes sense, so the chart
// type is locked to LINE (or, for the responses list, exempted entirely).
const isTrendGroupBy = (groupBy: unknown) => groupBy !== 'single';

const emptyFormObj = (
  allQuestions: ChartableQuestion[]
): Record<string, unknown> => ({
  name: '',
  questionUuid: allQuestions[0]?.uuid ?? '',
  groupBy: 'single',
  chartType: 'DOUGHNUT',
  periodGranularity: undefined,
  statement: undefined,
});

const formObjFromExisting = (
  existing: SurveyChartConfig
): Record<string, unknown> => ({
  name: existing.name,
  questionUuid: existing.config.questionUuid,
  groupBy: existing.config.groupBy,
  chartType: existing.config.chartType,
  periodGranularity: existing.config.periodGranularity,
  statement: existing.config.statement,
});

// Add/edit one report chart: pick a question, how to group it, how to
// render it, and an optional title. A pure form - it hands the transformed
// {name, config} to onSave and lets the caller (CustomSurveyReportBuilder)
// own the actual create/update request.
const SurveyChartBuilderModal: FC<Props> = ({
  isOpen,
  toggle,
  onSave,
  chartableQuestions,
  allQuestions,
  existing,
}) => {
  const { formatMessage } = useIntl();

  const [formObj, setFormObj] = useState<Record<string, unknown>>(
    emptyFormObj(allQuestions)
  );

  // Reset the form whenever the modal (re)opens, matching whichever chart
  // (or none, for create) it's opening for. Deliberately excludes
  // allQuestions - the parent may refetch (a new array reference) while
  // the modal is open, and that must not reset an in-progress edit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency omission
  useEffect(() => {
    if (isOpen) {
      setFormObj(
        existing ? formObjFromExisting(existing) : emptyFormObj(allQuestions)
      );
    }
  }, [isOpen, existing]);

  const chartType = (formObj.chartType as ChartType) ?? 'DOUGHNUT';
  const groupBy = formObj.groupBy;
  const isResponses = chartType === 'RESPONSES';
  const isTrend = !isResponses && isTrendGroupBy(groupBy);

  // A trend has one axis for time, so a multi-statement LIKERT can only
  // plot one of its statements at a time.
  const statements = useMemo(
    () =>
      allQuestions.find((q) => q.uuid === formObj.questionUuid)?.statements ??
      [],
    [allQuestions, formObj.questionUuid]
  );
  const needsStatement = isTrend && statements.length > 0;

  const chartTypeOptions = useMemo(() => {
    const allOptions = CHART_TYPE_OPTIONS(formatMessage);
    const allowedIds = isTrend
      ? new Set(['LINE', 'RESPONSES'])
      : new Set(['DOUGHNUT', 'BAR', 'HORIZONTAL_BAR', 'RESPONSES']);
    return allOptions.filter((o) => allowedIds.has(o.id));
  }, [formatMessage, isTrend]);

  const handleValidate = useCallback(
    (obj: Record<string, unknown>) => {
      const errors: Record<string, string> = {};
      if (!obj.questionUuid) {
        errors.questionUuid = formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.validation.question_required',
          defaultMessage: 'Question is required.',
        });
      } else if (
        obj.chartType !== 'RESPONSES' &&
        !chartableQuestions.some((q) => q.uuid === obj.questionUuid)
      ) {
        // Some question types (free-text, date picker) have no discrete
        // values to distribute, so they can only ever be charted via
        // RESPONSES (a plain answers table).
        errors.chartType = formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.validation.question_not_chartable',
          defaultMessage:
            'This question type can only be charted as Responses (table).',
        });
      }
      if (needsStatement && !obj.statement) {
        errors.statement = formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.validation.statement_required',
          defaultMessage: 'Statement is required.',
        });
      }
      return errors;
    },
    [formatMessage, chartableQuestions, needsStatement]
  );

  const transformObjectBeforeSubmit = useCallback(
    (
      obj: Record<string, unknown>
    ): { name: string; config: SurveyChartConfigBody } => {
      const objChartType = obj.chartType as ChartType;
      const config: SurveyChartConfigBody =
        objChartType === 'RESPONSES'
          ? {
              questionUuid: obj.questionUuid as string,
              chartType: 'RESPONSES',
              groupBy: 'single',
            }
          : {
              questionUuid: obj.questionUuid as string,
              chartType: objChartType,
              groupBy: obj.groupBy as SurveyChartConfigBody['groupBy'],
              ...(obj.groupBy === 'by_period'
                ? {
                    periodGranularity:
                      obj.periodGranularity as SurveyChartConfigBody['periodGranularity'],
                  }
                : {}),
              ...(needsStatement ? { statement: obj.statement as string } : {}),
            };
      return { name: ((obj.name as string) ?? '').trim(), config };
    },
    [needsStatement]
  );

  const inputs = useMemo(
    () => [
      {
        name: 'questionUuid',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.question',
          defaultMessage: 'Question',
        }),
        objects: allQuestions.map((q) => ({
          id: q.uuid,
          name: q.label,
          retired: q.retired,
        })),
        required: true,
        clearable: false,
        buttonClassName: 'w-100 text-start d-flex align-items-center',
        onChangeSideEffects: (
          currentObject: Record<string, unknown>,
          newValue: string
        ) =>
          currentObject.questionUuid === newValue
            ? {}
            : { statement: undefined },
        renderItem: (o: { name: string; retired: boolean }) => (
          <div>
            {/* expanded avoids RichTextViewer's "View more" toggle - a
                <button> that would otherwise nest inside the dropdown's own
                <button> (DropdownToggle/DropdownItem) while its height-check
                effect hasn't run yet. */}
            <RichTextViewer model={o.name} expanded />
            {o.retired && (
              <div className="small text-muted">
                {formatMessage({
                  id: 'app.views.administration.custom_surveys.charts.builder_modal.question_retired',
                  defaultMessage: 'No longer on the survey',
                })}
              </div>
            )}
          </div>
        ),
      },
      {
        name: 'groupBy',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.group_by',
          defaultMessage: 'Group by',
        }),
        objects: CHART_GROUP_BY_OPTIONS(formatMessage),
        required: true,
        clearable: false,
        buttonClassName: 'w-100 text-start',
        formGroupStyle: isResponses ? { display: 'none' } : undefined,
        onChangeSideEffects: (
          currentObject: Record<string, unknown>,
          newValue: string
        ) => {
          if (currentObject.groupBy === newValue) return {};
          return isTrendGroupBy(newValue)
            ? { chartType: 'LINE', periodGranularity: undefined }
            : { chartType: 'DOUGHNUT' };
        },
      },
      {
        name: 'periodGranularity',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.period',
          defaultMessage: 'Period',
        }),
        objects: CHART_PERIOD_OPTIONS(formatMessage),
        required: groupBy === 'by_period',
        clearable: false,
        buttonClassName: 'w-100 text-start',
        formGroupStyle:
          isResponses || groupBy !== 'by_period'
            ? { display: 'none' }
            : undefined,
      },
      {
        name: 'chartType',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.chart_type',
          defaultMessage: 'Chart type',
        }),
        objects: chartTypeOptions,
        required: true,
        clearable: false,
        buttonClassName: 'w-100 text-start',
        disabled: isTrend,
        onChangeSideEffects: (
          currentObject: Record<string, unknown>,
          newValue: string
        ) => {
          if (currentObject.chartType === newValue) return {};
          if (newValue === 'RESPONSES') {
            return { groupBy: 'single', periodGranularity: undefined };
          }
          return {};
        },
      },
      {
        name: 'statement',
        type: INPUT_TYPES.DROPDOWN,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.statement',
          defaultMessage: 'Statement',
        }),
        objects: statements.map((s) => ({ id: s.name, name: s.label })),
        required: needsStatement,
        clearable: false,
        buttonClassName: 'w-100 text-start',
        formGroupStyle: needsStatement ? undefined : { display: 'none' },
      },
      {
        name: 'name',
        type: INPUT_TYPES.TEXT,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder_modal.title',
          defaultMessage: 'Title (optional)',
        }),
      },
    ],
    [
      formatMessage,
      allQuestions,
      isResponses,
      groupBy,
      chartTypeOptions,
      isTrend,
      statements,
      needsStatement,
    ]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={
        existing
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.charts.builder_modal.edit_title',
              defaultMessage: 'Edit chart',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.charts.builder_modal.add_title',
              defaultMessage: 'Add chart',
            })
      }
      object={formObj}
      inputs={inputs}
      onChange={setFormObj}
      onValidate={handleValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.custom_surveys.charts.builder_modal.save',
        defaultMessage: 'Save',
      })}
      disableUnsavedChangesPrompt
      noValidate
      callback={onSave}
    />
  );
};

export default SurveyChartBuilderModal;
